<script>
  import FileUpload from '../common/FileUpload.svelte';
  import { API_BASE_URL } from '../../config.js';
  import BaseModal from '../modals/BaseModal.svelte';
  import ConfirmModal from '../modals/ConfirmModal.svelte';
  import InfoModal from '../modals/InfoModal.svelte';
  import Button from '../common/Button.svelte';

  export let user = null;
  export let mypageTab = 'applications';
  export let applications = [];
  export let filteredApplications = [];
  export let appStats = {};
  export let activeFilter = 'all';
  export let profileForm = {};
  export let userFiles = [];
  export let calculatedAge = null;
  export let profileUpdateMessage = '';
  export let isProfileUpdating = false;
  
  // 경력 데이터 디버깅을 위한 reactive statement
  $: {
    console.log('[DEBUG][MyPage] profileForm career data:', {
      careerYear: profileForm.careerYear,
      careerMonth: profileForm.careerMonth,
      careerYearType: typeof profileForm.careerYear,
      careerMonthType: typeof profileForm.careerMonth
    });
  }
  
  // 경력 구분 자동 선택을 위한 reactive statements
  $: careerYearNum = Number(profileForm.careerYear) || 0;
  $: careerMonthNum = Number(profileForm.careerMonth) || 0;
  $: isNewcomer = careerYearNum === 0 && careerMonthNum === 0;
  $: isExperienced = careerYearNum > 0 || careerMonthNum > 0;
  
  // 경력 선택 상태 디버깅
  $: {
    console.log('[DEBUG][MyPage] Career selection state:', {
      careerYearNum,
      careerMonthNum,
      isNewcomer,
      isExperienced
    });
  }
  export let statusOptions = [];
  export let statusMapping = {};
  export let reverseStatusMapping = {};
  export let onSetMypageTab = () => {};
  export let onSetFilter = () => {};
  export let onUpdateUserProfile = () => {};
  export let onHandleFileUploadSuccess = () => {};
  export let onHandleFileDownload = () => {};
  export let onHandleFileDelete = () => {};
  export let onCancelApplication = () => {};
  export let passwordUpdateMessage = '';
  export let isPasswordUpdating = false;

  // 관리자 계정 확인 함수
  function isAdminAccount(user) {
    return user && user.email === 'admin@visionwide.kr';
  }

  // 비밀번호 변경 폼 상태
  let passwordForm = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  };

  // 회원탈퇴 동의 체크박스 상태
  let withdrawalAgreed = false;

  // 회원탈퇴 처리 함수
  async function handleWithdrawal() {
    // 관리자 계정 보호
    if (isAdminAccount(user)) {
      alert('관리자 계정은 회원탈퇴할 수 없습니다.');
      return;
    }

    // 사용자 확인
    const confirmed = window.confirm('정말 회원탈퇴하시겠습니까?');
    if (!confirmed) return;

    const token = localStorage.getItem('token');
    if (!token || token === 'null' || token === 'undefined') {
      alert('로그인이 필요합니다.');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/me`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        alert(data.message || '회원탈퇴에 실패했습니다.');
        return;
      }

      alert('회원탈퇴가 완료되었습니다.');

      // 클라이언트 측 로그아웃 처리
      localStorage.removeItem('token');
      sessionStorage.clear();

      // 메인 페이지로 리다이렉트
      window.location.href = '/';
    } catch (error) {
      console.error('회원탈퇴 오류:', error);
      alert('서버 오류가 발생했습니다.');
    }
  }

  // 탭 변경 시 관리자 계정 보호
  function handleTabChange(tabName) {
    if (tabName === 'withdrawal' && isAdminAccount(user)) {
      alert('관리자 계정은 회원탈퇴 메뉴에 접근할 수 없습니다.');
      return;
    }
    onSetMypageTab(tabName);
  }

  // 상세보기 모달 상태
  let isDetailModalOpen = false;
  let selectedApplication = null;
  let projectDetail = null; // 상세보기용 프로젝트 정보
  let editableApplication = null; // 편집 가능한 지원 정보
  let isEditingApplication = false; // 편집 모드 상태

  // 지원 취소 관련 상태
  let isCancelConfirmOpen = false;
  let applicationToCancel = null;
  let isCanceling = false;
  let cancelResultMessage = '';
  let showCancelResult = false;

  // 상세보기 모달 열기 (디버깅 로그 포함)
  function handleOpenDetailModal(app) {
    console.log('[DEBUG][MyPage] handleOpenDetailModal 호출', app);
    selectedApplication = app;
    editableApplication = {
      price: app.price || '',
      message: app.message || '',
      available_date: app.available_date || '',
      start_work_date: app.start_work_date || ''
    };
    isEditingApplication = false;
    console.log('[DEBUG][MyPage] selectedApplication 설정', selectedApplication);
    isDetailModalOpen = true;
    console.log('[DEBUG][MyPage] isDetailModalOpen 상태', isDetailModalOpen);
    projectDetail = null; // 기존 데이터 초기화

    // 프로젝트 세부 정보를 불러옴 (토큰이 있을 때만)
    const token = localStorage.getItem('token');
    const projectId = app.project_id || app.projectId;
    if (projectId && token) {
      fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
        .then(res => res.json())
        .then(data => {
          if (data && data.project) {
            projectDetail = data.project;
          }
        })
        .catch(err => console.error('[MyPage] 프로젝트 상세 조회 오류', err));
    }
  }

  // 상세보기 모달 닫기
  function handleCloseDetailModal() {
    console.log('[DEBUG][MyPage] handleCloseDetailModal 호출');
    isDetailModalOpen = false;
    selectedApplication = null;
    editableApplication = null;
    isEditingApplication = false;
    console.log('[DEBUG][MyPage] 모달 상태 초기화', { isDetailModalOpen, selectedApplication });
  }
  
  // 편집 모드 토글
  function toggleEditMode() {
    isEditingApplication = !isEditingApplication;
    if (isEditingApplication) {
      // 편집 모드로 전환할 때 현재 값으로 초기화
      editableApplication = {
        price: selectedApplication.price || '',
        message: selectedApplication.message || '',
        available_date: selectedApplication.available_date || '',
        start_work_date: selectedApplication.start_work_date || ''
      };
    }
  }
  
  // 지원 정보 수정 저장
  async function handleSaveApplicationEdit() {
    if (!selectedApplication || !editableApplication) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token || token === 'null' || token === 'undefined') {
        alert('로그인이 필요합니다.');
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/applications/${selectedApplication.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          price: editableApplication.price ? parseInt(editableApplication.price) : null,
          message: editableApplication.message || null,
          available_date: editableApplication.available_date || null,
          start_work_date: editableApplication.start_work_date || null
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '지원 정보 수정에 실패했습니다.');
      }
      
      const data = await response.json();
      
      // 성공 시 로컬 데이터 업데이트
      selectedApplication.price = editableApplication.price;
      selectedApplication.message = editableApplication.message;
      selectedApplication.available_date = editableApplication.available_date;
      selectedApplication.start_work_date = editableApplication.start_work_date;
      
      isEditingApplication = false;
      alert('지원 정보가 수정되었습니다.');
      
      // 지원 목록 새로고침을 위해 부모 컴포넌트에 알림 (필요시)
      // if (onRefreshApplications) onRefreshApplications();
      
    } catch (error) {
      console.error('지원 정보 수정 오류:', error);
      alert(error.message || '지원 정보 수정 중 오류가 발생했습니다.');
    }
  }

  // 지원 취소 확인 모달 열기
  function handleOpenCancelConfirm(application) {
    applicationToCancel = application;
    isCancelConfirmOpen = true;
  }

  // 지원 취소 확인 모달 닫기
  function handleCloseCancelConfirm() {
    isCancelConfirmOpen = false;
    applicationToCancel = null;
  }

  // 취소 결과 모달 닫기
  function handleCloseCancelResult() {
    const isSuccess = cancelResultMessage.includes('취소되었습니다');
    showCancelResult = false;
    cancelResultMessage = '';
    // 성공인 경우 상세 모달도 닫기
    if (isSuccess) {
      handleCloseDetailModal();
    }
  }

  // 지원 취소 실행
  async function handleConfirmCancel() {
    if (!applicationToCancel) return;

    try {
      isCanceling = true;
      const token = localStorage.getItem('token');
      
      if (!token || token === 'null' || token === 'undefined') {
        cancelResultMessage = '로그인이 필요합니다.';
        showCancelResult = true;
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/applications/${applicationToCancel.id}/cancel`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      const data = await response.json();

      if (!response.ok) {
        cancelResultMessage = data.message || '지원 취소에 실패했습니다.';
        showCancelResult = true;
        return;
      }

      // 성공 메시지 표시
      cancelResultMessage = '지원이 취소되었습니다.';
      showCancelResult = true;
      
      // 지원 목록 새로고침을 위해 부모 컴포넌트의 함수 호출
      if (onCancelApplication) {
        onCancelApplication(applicationToCancel.id);
      }

    } catch (error) {
      console.error('지원 취소 오류:', error);
      cancelResultMessage = '서버 오류가 발생했습니다.';
      showCancelResult = true;
    } finally {
      isCanceling = false;
      isCancelConfirmOpen = false;
    }
  }

  // 다음 주소 API 호출 함수
  function openDaumPostcode() {
    if (typeof window.daum === 'undefined' || typeof window.daum.Postcode === 'undefined') {
      // 다음 주소 API 스크립트가 로드되지 않은 경우 동적으로 로드
      const script = document.createElement('script');
      script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
      script.onload = () => {
        setTimeout(openDaumPostcode, 100); // 스크립트 로드 후 재호출
      };
      document.head.appendChild(script);
      return;
    }

    new window.daum.Postcode({
      oncomplete: function(data) {
        // 각 주소의 노출 규칙에 따라 주소를 조합한다.
        let addr = ''; // 주소 변수
        let extraAddr = ''; // 참고항목 변수

        //사용자가 선택한 주소 타입에 따라 해당 주소 값을 가져온다.
        if (data.userSelectedType === 'R') { // 사용자가 도로명 주소를 선택했을 경우
          addr = data.roadAddress;
        } else { // 사용자가 지번 주소를 선택했을 경우(J)
          addr = data.jibunAddress;
        }

        // 사용자가 선택한 주소가 도로명 타입일때 참고항목을 조합한다.
        if(data.userSelectedType === 'R'){
          // 법정동명이 있을 경우 추가한다. (법정리는 제외)
          // 법정동의 경우 마지막 문자가 "동/로/가"로 끝난다.
          if(data.bname !== '' && /[동|로|가]$/g.test(data.bname)){
            extraAddr += data.bname;
          }
          // 건물명이 있고, 공동주택일 경우 추가한다.
          if(data.buildingName !== '' && data.apartment === 'Y'){
            extraAddr += (extraAddr !== '' ? ', ' + data.buildingName : data.buildingName);
          }
          // 표시할 참고항목이 있을 경우, 괄호까지 추가한 최종 문자열을 만든다.
          if(extraAddr !== ''){
            extraAddr = ' (' + extraAddr + ')';
          }
          // 조합된 참고항목을 해당 필드에 넣는다.
          addr += extraAddr;
        }

        // 우편번호와 주소 정보를 필드에 넣는다.
        profileForm.zipcode = data.zonecode;
        profileForm.basicAddress = addr;

        // 커서를 상세주소 필드로 이동한다.
        setTimeout(() => {
          const detailAddressInput = document.querySelector('.detail-address-input');
          if (detailAddressInput) {
            detailAddressInput.focus();
          }
        }, 100);
      },
      // 우편번호 검색 후 주소 클릭 시 닫힘
      onclose: function(state) {
        //state를 이용하여 팝업이 닫힐 때의 상태를 확인할 수 있다.
        if (state === 'FORCE_CLOSE'){
          //사용자가 ESC를 눌러 팝업을 닫았을 경우
          //예를 들어, validation 로직 추가 가능
        } else if (state === 'COMPLETE_CLOSE'){
          //사용자가 주소를 선택하여 팝업이 닫혔을 경우
          //상세주소 필드로 포커스 이동은 이미 oncomplete에서 처리됨
        }
      }
    }).open({
      popupName: 'postcodePopup',
      popupKey: 'popup1' // 팝업창 Key 값 설정 (여러개의 팝업을 띄울 경우 중복되지 않게 설정)
    });
  }

  // 프로필 업데이트 핸들러 (경력기간 검증 포함)
  function handleUpdateProfile() {
    // 경력기간 검증
    if (careerYearNum === 0 && careerMonthNum === 0) {
      profileUpdateMessage = '경력기간은 필수값입니다. 경력을 입력해주세요.';
      return;
    }

    // 주소 일관성 검증
    if ((profileForm.zipcode && !profileForm.basicAddress) || (!profileForm.zipcode && profileForm.basicAddress)) {
      profileUpdateMessage = '주소 찾기 버튼을 눌러 정확한 주소를 입력해주세요.';
      return;
    }

    // 검증 통과 시 부모 컴포넌트의 업데이트 함수 호출
    onUpdateUserProfile();
  }

  // 비밀번호 변경 핸들러
  async function handleChangePassword() {
    // 클라이언트 검증
    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    const passwordPattern = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{6,15}$/;

    if (!currentPassword || !newPassword || !confirmPassword) {
      passwordUpdateMessage = '모든 필드를 입력해주세요.';
      return;
    }

    if (newPassword !== confirmPassword) {
      passwordUpdateMessage = '신규 비밀번호와 확인 값이 일치하지 않습니다.';
      return;
    }

    if (!passwordPattern.test(newPassword)) {
      passwordUpdateMessage = '비밀번호는 영문, 숫자, 특수기호 중 2가지 이상 조합으로 6~15자여야 합니다.';
      return;
    }

    try {
      isPasswordUpdating = true;
      passwordUpdateMessage = '';

      const token = localStorage.getItem('token');
      if (!token || token === 'null' || token === 'undefined') {
        passwordUpdateMessage = '로그인이 필요합니다.';
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/users/me/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: currentPassword,
          newPassword: newPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        passwordUpdateMessage = data.message || '비밀번호 변경에 실패했습니다.';
        return;
      }

      passwordUpdateMessage = '비밀번호가 성공적으로 변경되었습니다.';
      // 폼 초기화
      passwordForm.currentPassword = '';
      passwordForm.newPassword = '';
      passwordForm.confirmPassword = '';

    } catch (error) {
      console.error('비밀번호 변경 오류:', error);
      passwordUpdateMessage = '서버 오류가 발생했습니다.';
    } finally {
      isPasswordUpdating = false;
    }
  }

  function getProjectTitle(app) {
    return app.project_title || app.projectTitle || app.projectName || app.title || 
           (app.project && app.project.title) || '프로젝트명 없음';
  }

  function getApplicationDate(app) {
    const dateValue = app.appliedAt || app.applied_at || app.createdAt || app.created_at || app.date;
    return dateValue ? new Date(dateValue).toLocaleDateString() : '-';
  }

  function getStatusDisplay(app) {
    if (typeof app.status === 'number') {
      const statusOption = statusOptions.find(option => option.id === app.status);
      return {
        label: statusOption?.label || '알 수 없음',
        class: reverseStatusMapping[app.status] || ''
      };
    } else if (typeof app.status === 'string' && statusMapping[app.status] !== undefined) {
      const statusOption = statusOptions.find(option => option.id === statusMapping[app.status]);
      return {
        label: statusOption?.label || '알 수 없음',
        class: app.status
      };
    } else {
      const statusMap = {
        'applied': '지원완료',
        'reviewing': '검토중',
        'closed': '마감',
        'canceled': '지원 취소'
      };
      return {
        label: statusMap[app.status] || app.status || '알 수 없음',
        class: app.status || 'applied'
      };
    }
  }

  function formatPrice(value) {
    if (value === null || value === undefined || value === '') return '-';
    const num = Number(value);
    return isNaN(num) ? value : num.toLocaleString();
  }

  function formatDate(value) {
    if (!value) return '-';
    return new Date(value).toLocaleDateString();
  }
</script>

<div class="mypage-container">
  <div class="mypage-sidebar">
    <h2>마이페이지</h2>
    <ul class="mypage-nav">
      <li class:active={mypageTab === 'applications'}>
        <button on:click={() => handleTabChange('applications')}>지원현황</button>
      </li>
      <li class:active={mypageTab === 'profile'}>
        <button on:click={() => handleTabChange('profile')}>정보수정</button>
      </li>
      <li class:active={mypageTab === 'password'}>
        <button on:click={() => handleTabChange('password')}>비밀번호 변경</button>
      </li>
      {#if !isAdminAccount(user)}
        <li class:active={mypageTab === 'withdrawal'}>
          <button on:click={() => handleTabChange('withdrawal')}>회원탈퇴</button>
        </li>
      {/if}
    </ul>
  </div>
  
  <div class="mypage-content-area">
    {#if mypageTab === 'applications'}
      <div class="mypage-header">
        <h2>지원현황</h2>
      </div>
      
      <div class="mypage-status">
        <div 
          class="status-item" 
          class:active={activeFilter === 'all'} 
          on:click={() => onSetFilter('all')}
          on:keydown={(e) => e.key === 'Enter' && onSetFilter('all')}
          tabindex="0"
          role="button"
        >
          <div class="status-label">전체</div>
          <div class="status-count">{appStats.total || 0}</div>
        </div>
        <div 
          class="status-item" 
          class:active={activeFilter === 'applied'} 
          on:click={() => onSetFilter('applied')}
          on:keydown={(e) => e.key === 'Enter' && onSetFilter('applied')}
          tabindex="0"
          role="button"
        >
          <div class="status-label">지원완료</div>
          <div class="status-count">{appStats.applied || 0}</div>
        </div>
        <div 
          class="status-item" 
          class:active={activeFilter === 'reviewing'} 
          on:click={() => onSetFilter('reviewing')}
          on:keydown={(e) => e.key === 'Enter' && onSetFilter('reviewing')}
          tabindex="0"
          role="button"
        >
          <div class="status-label">검토중</div>
          <div class="status-count">{appStats.reviewing || 0}</div>
        </div>
        <div 
          class="status-item" 
          class:active={activeFilter === 'proceeding'} 
          on:click={() => onSetFilter('proceeding')}
          on:keydown={(e) => e.key === 'Enter' && onSetFilter('proceeding')}
          tabindex="0"
          role="button"
        >
          <div class="status-label">진행중</div>
          <div class="status-count">{appStats.proceeding || 0}</div>
        </div>
        <div 
          class="status-item" 
          class:active={activeFilter === 'closed'} 
          on:click={() => onSetFilter('closed')}
          on:keydown={(e) => e.key === 'Enter' && onSetFilter('closed')}
          tabindex="0"
          role="button"
        >
          <div class="status-label">마감</div>
          <div class="status-count">{appStats.closed || 0}</div>
        </div>
        <div 
          class="status-item" 
          class:active={activeFilter === 'canceled'} 
          on:click={() => onSetFilter('canceled')}
          on:keydown={(e) => e.key === 'Enter' && onSetFilter('canceled')}
          tabindex="0"
          role="button"
        >
          <div class="status-label">지원취소</div>
          <div class="status-count">{appStats.canceled || 0}</div>
        </div>
      </div>
      
      {#if applications && applications.length > 0}
        <div class="applications-table">
          <div class="application-row table-header">
            <div class="application-cell">프로젝트명</div>
            <div class="application-cell">지원일</div>
            <div class="application-cell">상태</div>
            <div class="application-cell">바로가기</div>
          </div>
          
          {#each filteredApplications as app}
            <div class="application-row">
              <div class="application-cell">
                {getProjectTitle(app)}
              </div>
              <div class="application-cell">
                {getApplicationDate(app)}
              </div>
              <div class="application-cell">
                {#if getStatusDisplay(app)}
                  <span class="status-badge {getStatusDisplay(app).class}">
                    {getStatusDisplay(app).label}
                  </span>
                {/if}
              </div>
              <div class="application-cell">
                <button class="detail-button" on:click={() => handleOpenDetailModal(app)}>
                  상세
                </button>
              </div>
            </div>
          {/each}
        </div>
      {:else}
        <div class="empty-applications">
          <p>지원 내역이 없습니다.</p>
        </div>
      {/if}
    {:else if mypageTab === 'profile'}
      <div class="mypage-header">
        <h2>정보수정</h2>
      </div>
      
      <div class="mypage-content">
        <div class="profile-info">
          <h3>기본정보</h3>
          
          <div class="profile-form">
            <div class="form-row">
              <div class="form-label">가입일자</div>
              <div class="form-value">
                {new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-label">이름</div>
              <div class="form-input">
                <input type="text" bind:value={profileForm.name} placeholder="이름을 입력해주세요.">
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-label">이메일</div>
              <div class="form-value">
                {user ? user.email : ''}
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-label">연락처</div>
              <div class="form-input">
                <input type="text" bind:value={profileForm.phone} placeholder="연락처를 입력해주세요.">
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-label">투입가능일자</div>
              <div class="form-input">
                <input type="date" bind:value={profileForm.availableFrom} placeholder="투입가능일자를 선택해주세요.">
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-label">생년월일</div>
              <div class="form-input">
                <input type="date" bind:value={profileForm.birthDate} placeholder="생년월일을 입력해주세요.">
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-label">성별</div>
              <div class="form-input radio-group">
                <label class="radio-container" style="margin-right: 15px;">
                  <input type="radio" name="profileGender" bind:group={profileForm.gender} value="M">
                  <span class="radio-custom"></span>
                  <span class="label-text">남성</span>
                </label>
                <label class="radio-container">
                  <input type="radio" name="profileGender" bind:group={profileForm.gender} value="F">
                  <span class="radio-custom"></span>
                  <span class="label-text">여성</span>
                </label>
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-label">최종학력</div>
              <div class="form-input radio-group education-group">
                <label class="radio-container">
                  <input type="radio" name="profileEducation" bind:group={profileForm.educationLevel} value="highschool" required>
                  <span class="radio-custom"></span>
                  <span class="label-text">고등학교</span>
                </label>
                <label class="radio-container">
                  <input type="radio" name="profileEducation" bind:group={profileForm.educationLevel} value="associate">
                  <span class="radio-custom"></span>
                  <span class="label-text">전문학사</span>
                </label>
                <label class="radio-container">
                  <input type="radio" name="profileEducation" bind:group={profileForm.educationLevel} value="bachelor">
                  <span class="radio-custom"></span>
                  <span class="label-text">4년제학사</span>
                </label>
                <label class="radio-container">
                  <input type="radio" name="profileEducation" bind:group={profileForm.educationLevel} value="master">
                  <span class="radio-custom"></span>
                  <span class="label-text">석사이상</span>
                </label>
              </div>
            </div>
            
            <!-- 경력구분은 강제로 경력으로 고정 (UI에서 숨김) -->
            <div class="form-row" style="display: none;">
              <div class="form-label">경력 구분</div>
              <div class="form-input radio-group">
                <label class="radio-container">
                  <input 
                    type="radio" 
                    name="careerType" 
                    value="experienced"
                    checked={true}
                  >
                  <span class="radio-custom"></span>
                  <span class="label-text">경력</span>
                </label>
              </div>
            </div>
            
            <!-- 경력 기간 입력 (경력구분이 강제로 경력이므로 항상 표시) -->
            <div class="form-row">
              <div class="form-label">경력 기간</div>
              <div class="form-input career-input-group">
                <div class="career-input-item">
                  <input 
                    type="number" 
                    bind:value={profileForm.careerYear} 
                    placeholder="0"
                    min="0"
                    max="50"
                  >
                  <span class="career-unit">년</span>
                </div>
                <div class="career-input-item">
                  <input 
                    type="number" 
                    bind:value={profileForm.careerMonth} 
                    placeholder="0"
                    min="0"
                    max="11"
                  >
                  <span class="career-unit">개월</span>
                </div>
              </div>
              {#if careerYearNum === 0 && careerMonthNum === 0}
                <small class="form-text error-text">* 경력기간은 필수값입니다.</small>
              {/if}
            </div>
            
            {#if calculatedAge}
              <div class="form-row">
                <div class="form-label">나이 (자동 계산)</div>
                <div class="form-value age-display">
                  <span class="age-text">{calculatedAge}세</span>
                </div>
              </div>
            {/if}

            <!-- 추천자 이메일 -->
            <div class="form-row">
              <div class="form-label">추천자 이메일</div>
              <div class="form-value">
                {profileForm.recommenderEmail || '-'}
              </div>
            </div>

            <!-- 주소 입력 -->
            <div class="form-row">
              <div class="form-label">주소 (선택사항)</div>
              <div class="form-input address-input-group">
                <!-- 기본 주소 -->
                <input
                  type="text"
                  class="basic-address-input"
                  bind:value={profileForm.basicAddress}
                  placeholder="주소 검색을 클릭하여 기본 주소를 입력하세요"
                  readonly
                  on:click={openDaumPostcode}
                >

                <!-- 상세 주소 -->
                <input
                  type="text"
                  class="detail-address-input"
                  bind:value={profileForm.detailAddress}
                  placeholder="상세주소를 입력해주세요"
                >

                <!-- 우편번호와 주소찾기 버튼 -->
                <div class="zipcode-input-group">
                  <input
                    type="text"
                    class="zipcode-input"
                    bind:value={profileForm.zipcode}
                    placeholder="우편번호"
                    readonly
                  >
                  <button class="address-search-btn" type="button" on:click={openDaumPostcode}>
                    주소 검색
                  </button>
                </div>
              </div>
            </div>

              
            {#if profileUpdateMessage}
              <div class="update-message {profileUpdateMessage.includes('성공') ? 'success' : 'error'}">
                {profileUpdateMessage}
              </div>
            {/if}
            
            <div class="form-buttons">
              <button class="submit-btn" on:click={handleUpdateProfile} disabled={isProfileUpdating}>
                {#if isProfileUpdating}
                  수정 중...
                {:else}
                  수정하기
                {/if}
              </button>
            </div>
          </div>
          
          <!-- 파일 업로드 섹션 -->
          <div class="profile-info">
            <h3>첨부파일 관리</h3>
            
            <FileUpload
              fileType="profile"
              label="첨부 문서 업로드"
              description="이력카드 등을 업로드하세요.(이력카드는 워드 문서로 접수 가능)"
              maxFiles={5}
              existingFiles={userFiles}
              on:upload-success={onHandleFileUploadSuccess}
              on:download-file={onHandleFileDownload}
              on:delete-file={onHandleFileDelete}
            />
          </div>
        </div>
      </div>
    {:else if mypageTab === 'password'}
      <div class="mypage-header">
        <h2>비밀번호 변경</h2>
      </div>
      
      <div class="mypage-content">
        <div class="password-form">
          <div class="form-row">
            <div class="form-label">이메일</div>
            <div class="form-value">
              {user ? user.email : ''}
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-label">현재 비밀번호</div>
            <div class="form-input">
              <input type="password" bind:value={passwordForm.currentPassword} placeholder="현재 비밀번호를 입력해 주세요.">
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-label">신규 비밀번호</div>
            <div class="form-input">
              <input type="password" bind:value={passwordForm.newPassword} placeholder="6~15자 영문,숫자,특수기호 중 2가지 조합">
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-label">비밀번호 확인</div>
            <div class="form-input">
              <input type="password" bind:value={passwordForm.confirmPassword} placeholder="6~15자 영문,숫자,특수기호 중 2가지 조합">
            </div>
          </div>
          
          <div class="password-info">
            영문, 숫자, 특수기호 중 2가지 이상으로 조합하여 6자 이상 15자 이하로 입력해주세요.
          </div>
          
          {#if passwordUpdateMessage}
            <div class="update-message {passwordUpdateMessage.includes('성공') ? 'success' : 'error'}">
              {passwordUpdateMessage}
            </div>
          {/if}
          
          <div class="form-buttons">
            <button class="submit-btn" on:click={handleChangePassword} disabled={isPasswordUpdating}>
              {#if isPasswordUpdating}
                변경 중...
              {:else}
                변경하기
              {/if}
            </button>
          </div>
        </div>
      </div>
    {:else if mypageTab === 'withdrawal'}
      <div class="mypage-header">
        <h2>회원탈퇴</h2>
      </div>
      
      <div class="mypage-content">
        <div class="withdrawal-info">
          {#if isAdminAccount(user)}
            <div class="admin-notice">
              <h4>⚠️ 관리자 계정 안내</h4>
              <p>관리자 계정은 서비스 운영을 위해 회원탈퇴가 제한되어 있습니다.</p>
              <p>탈퇴가 필요한 경우 시스템 관리자에게 문의해주세요.</p>
            </div>
          {:else}
            <p>회원탈퇴 전에 안내사항을 꼭 확인해주세요.</p>
            
            <div class="notice-box">
              <h4>서비스 이용 기록 삭제</h4>
              <p>입사지원 정보, 입사지원 현황 등 취업에 필요한 서비스 이용 기록이 모두 삭제되며, 삭제된 데이터는 복구되지 않습니다. 필요한 데이터는 미리 백업을 해두시기 바랍니다.</p>
            </div>
            
            <div class="withdrawal-checkbox">
              <label>
                <input type="checkbox" bind:checked={withdrawalAgreed}> 안내사항을 모두 확인하였으며 이에 동의합니다.
              </label>
            </div>
            
            <div class="form-buttons">
              <button class="withdrawal-btn" on:click={handleWithdrawal} disabled={!withdrawalAgreed}>탈퇴하기</button>
            </div>
          {/if}
        </div>
      </div>
    {/if}
  </div>
</div>

<!-- 상세보기 모달 -->
{#if isDetailModalOpen && selectedApplication}
  <BaseModal bind:isOpen={isDetailModalOpen} title="지원 상세 정보" size="lg" on:close={handleCloseDetailModal}>
    <div class="detail-section">
      <h4>프로젝트 정보</h4>
      <div class="detail-grid">
        <div class="detail-item">
          <span class="detail-label">프로젝트명</span>
          <span class="detail-value">{getProjectTitle(selectedApplication)}</span>
        </div>
      </div>
    </div>

    <div class="detail-section">
      <h4>지원 정보</h4>
      <div class="detail-grid">
        <div class="detail-item">
          <span class="detail-label">지원일자</span>
          <span class="detail-value">{getApplicationDate(selectedApplication)}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">단가 (단위:만원)</span>
          <span class="detail-value">
            {#if isEditingApplication}
              <input 
                type="number" 
                bind:value={editableApplication.price} 
                placeholder="단가 입력" 
                class="edit-input"
              />
            {:else}
              {selectedApplication.price ? `${formatPrice(selectedApplication.price)}` : '-'}
            {/if}
          </span>
        </div>
        <div class="detail-item">
          <span class="detail-label">면접가능일</span>
          <span class="detail-value">
            {#if isEditingApplication}
              <input 
                type="text" 
                bind:value={editableApplication.available_date} 
                class="edit-input"
                placeholder="예: 2024-12-30"
              />
            {:else}
              {selectedApplication.available_date || '-'}
            {/if}
          </span>
        </div>
        <div class="detail-item">
          <span class="detail-label">출근가능일</span>
          <span class="detail-value">
            {#if isEditingApplication}
              <input 
                type="text" 
                bind:value={editableApplication.start_work_date} 
                class="edit-input"
                placeholder="예: 2024-12-30"
              />
            {:else}
              {selectedApplication.start_work_date || '-'}
            {/if}
          </span>
        </div>
        <div class="detail-item">
          <span class="detail-label">상태</span>
          <span class="detail-value">
            {#if getStatusDisplay(selectedApplication)}
              <span class="status-badge {getStatusDisplay(selectedApplication).class}">{getStatusDisplay(selectedApplication).label}</span>
            {/if}
          </span>
        </div>
      </div>
    </div>

    <div class="detail-section">
      <h4>지원 메시지</h4>
      {#if isEditingApplication}
        <textarea 
          bind:value={editableApplication.message} 
          placeholder="지원 메시지를 입력해주세요" 
          class="edit-textarea"
        ></textarea>
      {:else}
        <div class="message-box">{selectedApplication.message || '-'}</div>
      {/if}
    </div>

    <!-- 프로젝트 추가 상세 정보 -->
    {#if projectDetail}
      <div class="detail-section">
        <h4>프로젝트 세부 정보</h4>
        <div class="detail-grid">
          <div class="detail-item">
            <span class="detail-label">기업명</span>
            <span class="detail-value">{projectDetail.company || '-'}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">레벨</span>
            <span class="detail-value">{projectDetail.level || '-'}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">필요 인원</span>
            <span class="detail-value">{projectDetail.headcount || '-'}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">근무지</span>
            <span class="detail-value">{projectDetail.location || '-'}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">예상 단가 (단위:만원)</span>
            <span class="detail-value">{projectDetail.salary ? `${formatPrice(projectDetail.salary)}` : '-'}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">기간</span>
            <span class="detail-value">{formatDate(projectDetail.start_date)} ~ {formatDate(projectDetail.end_date)}</span>
          </div>
        </div>
      </div>
    {/if}

    <div slot="footer" class="detail-modal-actions">
      <Button variant="secondary" on:click={handleCloseDetailModal}>닫기</Button>
      {#if isEditingApplication}
        <Button variant="secondary" on:click={toggleEditMode}>취소</Button>
        <Button variant="primary" on:click={handleSaveApplicationEdit}>저장</Button>
      {:else}
        {#if typeof selectedApplication.status === 'string' ? (selectedApplication.status === 'applied' || selectedApplication.status === 'reviewing') : (selectedApplication.status === 0 || selectedApplication.status === 1)}
          <Button variant="primary" on:click={toggleEditMode}>수정</Button>
          <Button variant="danger" on:click={() => handleOpenCancelConfirm(selectedApplication)}>지원 취소</Button>
        {/if}
      {/if}
    </div>
  </BaseModal>
{/if}

<!-- 지원 취소 확인 모달 -->
{#if isCancelConfirmOpen && applicationToCancel}
  <ConfirmModal
    bind:isOpen={isCancelConfirmOpen}
    title="지원 취소 확인"
    message={`정말 '${getProjectTitle(applicationToCancel)}' 프로젝트에 대한 지원을 취소하시겠습니까?`}
    confirmText="취소하기"
    cancelText="돌아가기"
    danger={true}
    onConfirm={handleConfirmCancel}
    onCancel={handleCloseCancelConfirm}
  />
{/if}

<!-- 지원 취소 결과 모달 -->
{#if showCancelResult}
  <InfoModal
    bind:isOpen={showCancelResult}
    title={cancelResultMessage.includes('취소되었습니다') ? '취소 완료' : '취소 실패'}
    message={cancelResultMessage}
    type={cancelResultMessage.includes('취소되었습니다') ? 'success' : 'error'}
    okText="확인"
    onOk={handleCloseCancelResult}
  />
{/if}

<style>
  .mypage-container {
    display: flex;
    gap: 30px;
    margin: 30px 20px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    overflow: hidden;
  }

  .mypage-sidebar {
    width: 220px;
    flex-shrink: 0;
    padding: 30px 20px;
    border-right: 1px solid #eee;
  }

  .mypage-sidebar h2 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 30px;
    color: #333;
  }

  .mypage-nav {
    list-style: none;
  }

  .mypage-nav li {
    margin-bottom: 15px;
  }

  .mypage-nav button {
    background: none;
    border: none;
    color: #666;
    font-size: 1rem;
    text-align: left;
    cursor: pointer;
    padding: 0;
  }

  .mypage-nav li.active button {
    color: #3273dc;
    font-weight: 500;
  }

  .mypage-content-area {
    flex: 1;
    padding: 30px;
  }

  .mypage-header {
    margin-bottom: 25px;
  }

  .mypage-header h2 {
    font-size: 1.6rem;
    font-weight: 700;
    color: #333;
  }

  /* 지원현황 스타일 */
  .mypage-status {
    display: flex;
    background-color: white;
    border: 1px solid #eee;
    border-radius: 4px;
    margin-bottom: 30px;
    overflow: hidden;
  }

  .status-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px 10px;
    text-align: center;
    border-right: 1px solid #eee;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .status-item:hover {
    background-color: #f8f9fa;
  }

  .status-item.active {
    background-color: #e7f5ff;
  }

  .status-item.active .status-count {
    color: #1c7ed6;
  }

  .status-item:last-child {
    border-right: none;
  }

  .status-count {
    font-size: 1.5rem;
    font-weight: 600;
    color: #3273dc;
    margin-bottom: 5px;
  }

  .status-label {
    color: #666;
    font-size: 0.9rem;
  }

  .applications-table {
    width: 100%;
    border-top: 1px solid #ddd;
    border-bottom: 1px solid #ddd;
  }

  .application-row {
    display: flex;
    border-bottom: 1px solid #eee;
  }

  .application-row:last-child {
    border-bottom: none;
  }

  .application-cell {
    flex: 1;
    padding: 15px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.95rem;
  }

  .table-header {
    background-color: #f8f9fa;
    font-weight: 600;
  }

  .empty-applications {
    text-align: center;
    padding: 50px 0;
    color: #868e96;
    background-color: #f8f9fa;
    border-radius: 4px;
    margin-top: 20px;
  }

  .detail-button {
    background-color: #3273dc;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 5px 10px;
    font-size: 0.85rem;
    cursor: pointer;
  }

  .detail-button:hover {
    background-color: #2860b5;
  }

  /* 프로필 정보 폼 */
  .profile-info h3 {
    font-size: 1.2rem;
    font-weight: 600;
    margin-bottom: 20px;
    color: #333;
  }

  .profile-form {
    max-width: 600px;
  }

  .form-row {
    display: flex;
    margin-bottom: 20px;
    align-items: center;
  }

  .form-label {
    width: 120px;
    flex-shrink: 0;
    font-size: 0.95rem;
    color: #666;
  }

  .form-value {
    flex: 1;
    font-size: 1rem;
  }

  .form-input {
    flex: 1;
  }

  .form-input.radio-group {
    display: flex;
    align-items: center;
    min-height: 40px;
  }

  .form-input input {
    width: 100%;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
  }

  .radio-container {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
  }

  .radio-custom {
    width: 16px;
    height: 16px;
    border: 1px solid #ddd;
    border-radius: 50%;
    margin-right: 8px;
    position: relative;
  }

  .radio-container input[type="radio"] {
    display: none;
  }

  .radio-container input[type="radio"]:checked + .radio-custom::after {
    content: '';
    width: 8px;
    height: 8px;
    background-color: #3273dc;
    border-radius: 50%;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .label-text {
    font-size: 0.95rem;
  }

  /* 최종학력 라디오 그룹 스타일 */
  .education-group {
    flex-wrap: wrap;
    gap: 15px;
  }

  .education-group .radio-container {
    margin-right: 15px;
    margin-bottom: 8px;
  }

  /* 폼 텍스트 안내 */
  .form-text {
    display: block;
    margin-top: 5px;
    font-size: 0.8rem;
    color: #666;
    font-style: italic;
  }

  .form-text.error-text {
    color: #e03131;
    font-weight: 500;
  }

  .age-display {
    display: flex;
    align-items: center;
    padding: 10px 15px;
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 5px;
  }

  .age-text {
    font-size: 1rem;
    font-weight: 500;
    color: #3273dc;
  }
  
  /* 경력 입력 그룹 스타일 */
  .career-input-group {
    display: flex;
    align-items: center;
    gap: 15px;
  }
  
  .career-input-item {
    display: flex;
    align-items: center;
    gap: 5px;
  }
  
  .career-input-item input {
    width: 80px;
    text-align: center;
  }
  
  .career-unit {
    font-size: 0.9rem;
    color: #666;
    font-weight: 500;
  }

  /* 주소 입력 그룹 스타일 */
  .address-input-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }

  .zipcode-input-group {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    margin-bottom: 8px;
  }

  .zipcode-input {
    width: 120px !important;
    flex-shrink: 0;
    height: 40px;
  }

  .zipcode-input:read-only {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }

  .basic-address-input:read-only {
    background-color: #f8f9fa;
    cursor: not-allowed;
    height: 40px;
  }

  .detail-address-input {
    height: 40px;
  }

  .address-search-btn {
    height: 40px;
    padding: 0 16px;
    background-color: #3273dc;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s ease;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .address-search-btn:hover {
    background-color: #2366c1;
  }

  .address-search-btn:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(50, 115, 220, 0.3);
  }

  .form-buttons {
    margin-top: 20px;
    display: flex;
    justify-content: center;
  }

  .submit-btn {
    background-color: #3273dc;
    color: white;
    border: none;
    padding: 10px 24px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1rem;
  }

  .submit-btn:hover {
    background-color: #2366d1;
  }

  .submit-btn:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }

  .submit-btn:disabled:hover {
    background-color: #6c757d;
  }

  /* 업데이트 메시지 스타일 */
  .update-message {
    padding: 12px 16px;
    border-radius: 4px;
    margin: 15px 0;
    font-size: 0.9rem;
    font-weight: 500;
  }

  .update-message.success {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }

  .update-message.error {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }

  /* 비밀번호 변경 */
  .password-info {
    margin: 15px 0 30px;
    color: #666;
    font-size: 0.9rem;
    padding-left: 120px;
  }

  /* 회원탈퇴 */
  .withdrawal-info p {
    margin-bottom: 20px;
    color: #666;
  }

  .notice-box {
    background-color: #f9f9f9;
    border: 1px solid #eee;
    border-radius: 4px;
    padding: 20px;
    margin-bottom: 30px;
  }

  .notice-box h4 {
    font-size: 1.1rem;
    margin-bottom: 10px;
    color: #333;
  }

  .notice-box p {
    font-size: 0.95rem;
    line-height: 1.5;
    color: #666;
  }

  .withdrawal-checkbox {
    margin: 20px 0 30px;
  }

  .withdrawal-checkbox label {
    display: flex;
    align-items: center;
    font-size: 0.95rem;
    color: #666;
  }

  .withdrawal-checkbox input[type="checkbox"] {
    margin-right: 10px;
  }

  .withdrawal-btn {
    background-color: #ff3860;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 12px 30px;
    font-size: 1rem;
    cursor: pointer;
  }

  .withdrawal-btn:hover:not(:disabled) {
    background-color: #e03253;
  }

  .withdrawal-btn:disabled {
    background-color: #ddd;
    color: #999;
    cursor: not-allowed;
  }

  /* 관리자 안내 메시지 */
  .admin-notice {
    background-color: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 6px;
    padding: 20px;
    margin-bottom: 20px;
    text-align: center;
  }

  .admin-notice h4 {
    color: #856404;
    margin-bottom: 12px;
    font-size: 1.1rem;
  }

  .admin-notice p {
    color: #856404;
    font-size: 0.95rem;
    line-height: 1.5;
    margin-bottom: 8px;
  }

  .admin-notice p:last-child {
    margin-bottom: 0;
  }

  .status-badge {
    display: inline-block;
    font-size: 0.75rem;
    font-weight: 500;
    padding: 3px 8px;
    border-radius: 12px;
  }

  .status-badge.applied {
    background-color: #e7f5ff;
    color: #3273dc;
  }

  .status-badge.reviewing {
    background-color: #fff9db;
    color: #f59f00;
  }

  .status-badge.proceeding {
    background-color: #d3f9d8;
    color: #40c057;
  }

  .status-badge.accepted {
    background-color: #b2f2bb;
    color: #2b8a3e;
  }

  .status-badge.rejected {
    background-color: #ffe3e3;
    color: #e03131;
  }

  .status-badge.project_canceled,
  .status-badge.canceled {
    background-color: #ffe3e3;
    color: #fa5252;
  }

  .status-badge.project_delayed {
    background-color: #e5dbff;
    color: #7950f2;
  }

  .status-badge.closed {
    background-color: #f1f3f5;
    color: #868e96;
  }

  @media (max-width: 768px) {
    .mypage-container {
      flex-direction: column;
    }

    .mypage-sidebar {
      width: 100%;
      border-right: none;
      border-bottom: 1px solid #eee;
    }

    .mypage-status {
      flex-direction: column;
    }

    .status-item {
      flex-direction: row;
      justify-content: space-between;
      padding: 10px 0;
      border-bottom: 1px solid #eee;
      border-right: none;
    }

    .status-item:last-child {
      border-bottom: none;
    }

    .table-header {
      display: none;
    }
  }

  /* 상세 모달 레이아웃 개선 */
  .detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px 20px;
    margin-top: 8px;
  }

  .detail-item {
    display: flex;
    flex-direction: column;
    line-height: 1.4;
  }

  .detail-label {
    font-size: 0.8rem;
    color: #666;
    margin-bottom: 2px;
  }

  .detail-value {
    font-size: 0.95rem;
    font-weight: 500;
    color: #333;
  }

  /* 편집 모드 스타일 */
  .edit-input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.9rem;
    background-color: #fff;
  }

  .edit-textarea {
    width: 100%;
    min-height: 80px;
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.9rem;
    font-family: inherit;
    resize: vertical;
    background-color: #fff;
  }

  .edit-input:focus,
  .edit-textarea:focus {
    outline: none;
    border-color: #3273dc;
    box-shadow: 0 0 0 2px rgba(50, 115, 220, 0.2);
  }
</style>